/**
 * Parse a barcode scanner emission that may contain a full label with multiple fields.
 *
 * Supported formats (auto-detected):
 *   1. Delimited "BARKOD|ISIM|EBAT|GELIS_TARIHI"  (pipes, semicolons, tabs, or newlines)
 *   2. GS1-128 with common AIs: (01) GTIN, (10) LOT, (11) prod date YYMMDD,
 *      (17) expiry, (21) serial, (240) product identifier.
 *      For our use: barcode = (01) or (21) or (240) if present; date = (11) reformatted.
 *   3. Plain barcode (fallback) — treated as the whole thing.
 *
 * Return shape:
 *   { barkod, isim?, ebat?, gelis_tarihi? }  (undefined fields simply not present)
 *
 * The date is normalized to ISO YYYY-MM-DD if it looks like YYMMDD or DDMMYYYY.
 */

const DELIM_RE = /[|;\t\r\n]+/;

const isEbatLike = (s) => /^\d{2,5}\s*[xX*]\s*\d{2,5}$/.test(s.trim());

const normalizeDate = (s) => {
  if (!s) return undefined;
  const t = s.trim();
  // YYYY-MM-DD
  let m = t.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  // DD.MM.YYYY or DD/MM/YYYY
  m = t.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // YYMMDD (GS1)
  m = t.match(/^(\d{2})(\d{2})(\d{2})$/);
  if (m) {
    const yy = parseInt(m[1], 10);
    const year = yy >= 70 ? 1900 + yy : 2000 + yy;
    return `${year}-${m[2]}-${m[3]}`;
  }
  return undefined;
};

const parseGs1 = (raw) => {
  // GS1-128: fields prefixed by AI in parentheses like "(01)0123...(11)230415(21)XYZ"
  // Some scanners emit without parens using FNC1 (\x1D) between variable-length fields.
  // We only handle the paren form (most common HID output).
  const out = {};
  if (!raw.includes("(")) return null;
  const re = /\((\d{2,4})\)([^()\x1D]+)/g;
  let matched = false;
  let m;
  while ((m = re.exec(raw)) !== null) {
    matched = true;
    const ai = m[1];
    const val = m[2].trim();
    if (ai === "01" || ai === "21" || ai === "240") {
      out.barkod = out.barkod || val;
    } else if (ai === "11" || ai === "17") {
      const d = normalizeDate(val);
      if (d) out.gelis_tarihi = out.gelis_tarihi || d;
    } else if (ai === "10") {
      out.aciklama = out.aciklama || val;
    }
  }
  return matched && out.barkod ? out : null;
};

export function parseBarcodeLabel(raw) {
  if (raw == null) return { barkod: "" };
  const text = String(raw).trim();
  if (!text) return { barkod: "" };

  // 1. GS1 format
  const gs1 = parseGs1(text);
  if (gs1) return gs1;

  // 2. Delimited multi-field
  const parts = text.split(DELIM_RE).map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const out = { barkod: parts[0] };
    // Try to intelligently assign fields — barkod is always first,
    // then the rest go through isim / ebat / gelis_tarihi based on heuristics.
    const remaining = parts.slice(1);
    for (const p of remaining) {
      if (!out.gelis_tarihi) {
        const d = normalizeDate(p);
        if (d) {
          out.gelis_tarihi = d;
          continue;
        }
      }
      if (!out.ebat && isEbatLike(p)) {
        out.ebat = p.toUpperCase().replace(/\s+/g, "");
        continue;
      }
      if (!out.isim) {
        out.isim = p;
        continue;
      }
      // extra fields go into aciklama
      out.aciklama = (out.aciklama ? out.aciklama + " " : "") + p;
    }
    return out;
  }

  // 3. Plain barcode fallback
  return { barkod: text };
}
